import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import BasicCRUD from "../../core/basic-crud";
import FadeAlert from "../../core/fade-alert";
import AppContext from "../../../app-context";
import { socketUseEffect, socket, requestData } from "../../../socket";
import { getShortTimeZoneLabel } from "../../../utils/timezone";
import { PRODUCT_ID } from "../../../const";

const statusFilterOptions = [
    { value: "all", labelKey: "filterAll" },
    { value: "active", labelKey: "filterActive" },
    { value: "inactive", labelKey: "filterInactive" },
];

const normalizeTimeZones = (payload) => {
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload)) return payload;
    return [];
};

const UsersListTab = ({ t, lang, user, isMemberRole, openInviteDialog }) => {
    const object = "users";
    const [response, setResponse] = useState({});
    const [statusResponse, setStatusResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});
    const [timeZones, setTimeZones] = useState([]);
    const [timeZonesLoading, setTimeZonesLoading] = useState(false);
    const [timeZonesMap, setTimeZonesMap] = useState({});
    const [statusFilter, setStatusFilter] = useState("active");
    const statusFilterRef = useRef("active");

    useEffect(() => {
        if (statusFilterRef.current !== statusFilter) {
            AppContext.pages = AppContext.pages || {};
            AppContext.pages[object] = { ...(AppContext.pages[object] || {}), pageNum: 1 };
            statusFilterRef.current = statusFilter;
        }
    }, [statusFilter]);

    useEffect(() => {
        if (!user?.token) {
            setTimeZones([]);
            setTimeZonesLoading(false);
            return undefined;
        }

        setTimeZonesLoading(true);

        const listeners = [
            {
                name: "time_zones",
                fn: "list",
                args: { items_count: 500 },
                saveDataFN: (payload) => {
                    const list = normalizeTimeZones(payload);
                    const map = {};
                    list.forEach((tz) => {
                        const id = tz?.id ?? tz?.time_zone;
                        const label = tz?.name && tz?.time_zone
                            ? `${tz.name} (${tz.time_zone})`
                            : (tz?.name || tz?.time_zone || "");
                        if (!id || !label) return;
                        map[String(id)] = label;
                    });
                    setTimeZones(list);
                    setTimeZonesMap(map);
                    setTimeZonesLoading(false);
                },
            },
        ];

        const cleanup = socketUseEffect(listeners, user);
        return () => {
            setTimeZonesLoading(false);
            cleanup && cleanup();
        };
    }, [user?.token, user?.user_id]);

    const statusSelectOptions = useMemo(
        () =>
            statusFilterOptions.map((option) => ({
                value: option.value,
                label: t[option.labelKey] || option.value,
            })),
        [t]
    );

    const listArgs = useMemo(() => {
        const includeInactive = statusFilter !== "active";
        return {
            include_inactive: includeInactive ? 1 : 0,
            status_filter: statusFilter,
        };
    }, [statusFilter]);

    const timeZoneSelectOptions = useMemo(() => {
        if (timeZonesLoading) {
            return [{ id: "", name: t.tzLoading }];
        }

        if (!timeZones.length) {
            return [{ id: "", name: t.tzEmpty }];
        }

        const options = timeZones
            .map((tz) => {
                const optionId = tz?.id ?? tz?.time_zone ?? "";
                const label = tz?.name && tz?.time_zone
                    ? `${tz.name} (${tz.time_zone})`
                    : (tz?.name || tz?.time_zone || "");
                return {
                    id: optionId !== "" ? `${optionId}` : "",
                    name: label,
                };
            })
            .filter((tz) => tz.id && tz.name);

        return [{ id: "", name: t.tzSelect }, ...options];
    }, [timeZones, timeZonesLoading, t]);

    const userAddDisableConfig = isMemberRole ? { add: { disable: true, title: t.memberManageRestricted } } : null;

    const fields = {
        add: [
            {
                type: "tab",
                name: t.tabMain,
                image: "",
                fields: [
                    [
                        {
                            label: "",
                            type: "custom",
                            fn: () => (
                                <>
                                    {t.inviteLine1}<br />
                                    {t.inviteLine2}
                                </>
                            ),
                        },
                    ],
                    [
                        { label: t.fn, key: "first_name", default_value: "", showLabel: "top" },
                        { label: t.ln, key: "last_name", default_value: "", showLabel: "top" },
                    ],
                    [
                        { label: t.email, key: "email", default_value: "", showLabel: "top" },
                    ],
                ],
            },
        ],
        edit: [
            {
                type: "tab",
                name: t.tabMain,
                image: "",
                fields: [
                    [
                        { label: t.fn, key: "first_name", default_value: "", showLabel: "top" },
                        { label: t.ln, key: "last_name", default_value: "", showLabel: "top" },
                    ],
                    [
                        { label: t.email, key: "email", default_value: "", showLabel: "top" },
                        (() => {
                            const roleField = {
                                label: t.userRole,
                                key: "user_role",
                                default_value: "2",
                                type: "selectbox",
                                data: [
                                    { id: "admin", name: t.roleAdmin },
                                    { id: "member", name: t.roleMember },
                                ],
                                showLabel: "top",
                            };
                            const isEditingOwner = (fieldsData?.__orig_user_role || "").toLowerCase() === "owner";
                            if (isEditingOwner) {
                                return {
                                    ...roleField,
                                    type: "selectbox",
                                    data: [{ id: "owner", name: t.roleOwner }],
                                    default_value: "owner",
                                    value: "owner",
                                    disabled: true,
                                };
                            }
                            return roleField;
                        })(),
                    ],
                    [
                        { label: t.phone, key: "phone", default_value: "", showLabel: "top" },
                        { label: t.country, key: "country", default_value: "", showLabel: "top" },
                    ],
                    [
                        { label: t.timeZone, key: "time_zone", default_value: "", showLabel: "top", type: "selectbox", data: timeZoneSelectOptions },
                    ],
                ],
            },
        ],
    };

    const canDeleteUser = useCallback((targetUser) => {
        const role = (targetUser?.user_role || "").toLowerCase();
        const isOwner = role === "owner";
        const isSelf = Boolean(user?.user_id) && String(targetUser?.id || targetUser?.user_id) === String(user?.user_id);
        return !isOwner && !isSelf && !isMemberRole;
    }, [user?.user_id, isMemberRole]);

    const actions = [
        { name: "edit", label: t.edit, controller: "crud", icon: "ti ti-edit" },
        {
            name: "suspend",
            label: t.suspend,
            controller: "custom",
            icon: "ti ti-lock",
            fn: (_, data) => data?.active === true && canDeleteUser(data),
        },
        {
            name: "activate",
            label: t.activateAction,
            controller: "custom",
            icon: "ti ti-player-play",
            fn: (_, data) => data?.active === false && canDeleteUser(data),
        },
    ];

    const FullName = (data) => {
        const firstName = data['first_name'] || '';
        const lastName = data['last_name'] || '';
        const email = data['email'] || t.na;

        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span style={{ fontWeight: "bold", fontSize: 16, marginBottom: 4 }}>
                    {`${firstName} ${lastName}`.trim() || t.na}
                </span>
                <span style={{ fontSize: 12, color: "#6c757d" }}>{email}</span>
            </div>
        );
    };

    const formatUserRole = (data) => {
        const roleMap = {
            admin: t.roleAdmin,
            member: t.roleMember,
            owner: t.roleOwner,
        };
        return roleMap[data.user_role] || t.roleUnknown;
    };

    const formatTimeZone = (data) => {
        const timeZoneName = data?.time_zone_name || data?.timezone;
        if (timeZoneName) return getShortTimeZoneLabel(timeZoneName);

        const timeZoneId = data?.time_zone;
        if (timeZoneId === undefined || timeZoneId === null || timeZoneId === "") {
            return t.na;
        }

        const label = timeZonesMap[String(timeZoneId)] || "";
        return getShortTimeZoneLabel(label) || t.na;
    };

    const StatusBadge = ({ active }) => {
        const isActive = !!active;
        const label = isActive ? "Active" : "Suspended";
        const style = {
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            borderRadius: 12,
            fontWeight: 600,
            fontSize: 12,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            backgroundColor: isActive ? "#e9f7ef" : "#fff3cd",
            color: isActive ? "#1f7a3d" : "#b76e00",
            border: `1px solid ${isActive ? "#a3cfbb" : "#ffe69c"}`,
        };
        return (
            <span style={style}>
                <i className={`ti ${isActive ? "ti-circle-check" : "ti-lock"}`}></i>
                {label}
            </span>
        );
    };

    const formatStatusBadge = (item) => <StatusBadge active={item?.active} />;

    const columns = [
        { label: t.colName, key: "name", format: FullName },
        { label: t.colUserRole, key: "user_role", format: formatUserRole },
        { label: t.colTimeZone, key: "time_zone", format: formatTimeZone },
        { label: t.colRegDate, key: "reg_date" },
        { label: t.statusFilterLabel || "Status", key: "status", format: formatStatusBadge },
    ];

    const executeBeforeEdit = useCallback((data) => {
        const isOwner = (data?.user_role || "").toLowerCase() === "owner";
        return {
            ...data,
            __orig_user_role: data?.user_role,
            __is_owner: isOwner,
        };
    }, []);

    const executeBeforeSave = useCallback((data) => {
        const isOwner = data?.__is_owner || ((data?.__orig_user_role || "").toLowerCase() === "owner");
        const cleaned = { ...data };
        if (isOwner) {
            cleaned.user_role = data.__orig_user_role || "owner";
        }
        delete cleaned.__orig_user_role;
        delete cleaned.__is_owner;
        return cleaned;
    }, []);

    const handleStatusChange = async (action, item) => {
        if (!item?.id || !user?.token) return;
        if (action === "suspend") {
            const ok = window.confirm(t.confirmSuspend);
            if (!ok) return;
        }
        if (action === "activate") {
            const ok = window.confirm(t.confirmActivate);
            if (!ok) return;
        }

        const requestId = `users:status-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const responseEvent = "users:status-response";

        const handleResponse = (payload = {}) => {
            if (payload.request_id !== requestId) return;
            socket.off(responseEvent, handleResponse);
            if (payload.status === 1) {
                const successMessage = action === "suspend"
                    ? (t.suspendSuccess || t.inviteStatusSuccess)
                    : (t.activateSuccess || t.inviteStatusSuccess);
                setResponse({ status: 1, status_changed: 1, message: successMessage });
                setStatusResponse({ status: 1, message: successMessage });
                const targetPage = AppContext.pages?.[object]?.pageNum || 1;
                const args = { ...(listArgs || {}), page: targetPage };
                requestData(user, object, args, "list");
            } else {
                setResponse({ status: 0, err: { message: payload?.err?.message || t.inviteActionError } });
            }
        };

        socket.on(responseEvent, handleResponse);
        socket.emit("users:status", {
            request_id: requestId,
            token: user.token,
            product_id: PRODUCT_ID,
            company_id: user.company_id,
            target_user_id: item.id,
            action,
            page: AppContext.pages?.[object]?.pageNum || 1,
            items_count: AppContext.rows_per_page || 10,
            status_filter: statusFilter,
            simple_search_text: listArgs?.simple_search_text || "",
        });

        setTimeout(() => {
            socket.off(responseEvent, handleResponse);
        }, 10000);
    };

    const handleTableAction = (action, item) => {
        if (action === "suspend" || action === "activate") {
            handleStatusChange(action, item);
        }
    };

    const renderUserCard = (item, helpers) => {
        const fullNameBlock = FullName(item);
        const roleBlock = formatUserRole(item);
        const timeZoneBlock = formatTimeZone(item);
        const regDate = item.reg_date || t.na;
        const actionsList = (helpers.actions || []).filter(
            (action) => !action.fn || action.fn(action.name, item)
        );

        return (
            <div className="card h-100 shadow-sm border">
                <div className="card-body d-flex flex-column">
                    <div className="mb-2 d-flex justify-content-between align-items-start gap-2">
                        <div className="flex-grow-1">{fullNameBlock}</div>
                        {formatStatusBadge(item)}
                    </div>
                    <div className="text-muted small mb-2">{t.colUserRole}: <span className="fw-semibold text-dark">{roleBlock}</span></div>
                    <div className="text-muted small mb-2">{t.colTimeZone}: <span className="fw-semibold text-dark">{timeZoneBlock}</span></div>
                    <div className="text-muted small mb-4">{t.colRegDate}: <span className="fw-semibold text-dark">{regDate}</span></div>

                    <div className="mt-auto d-flex gap-2 justify-content-end flex-wrap">
                        {actionsList.map((action) => (
                            <button
                                key={action.name}
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                title={action.label}
                                onClick={() => helpers.tableAction?.(action.name, item)}
                            >
                                <i className={action.icon || "ti ti-dots"}></i>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const searchFieldsConfig = useMemo(() => ({
        simple: [
            {
                type: "tab",
                name: "",
                fields: [
                    [
                        {
                            type: "custom",
                            fn: () => (
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">{t.statusFilterLabel}</label>
                                    <select
                                        className="form-select"
                                        value={statusFilter}
                                        onChange={(event) => setStatusFilter(event.target.value)}
                                    >
                                        {statusSelectOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ),
                        },
                    ],
                    [
                        {
                            label: t.searchPlaceholder || "Search",
                            key: "text",
                            placeholder: t.searchPlaceholder || "Search",
                            beforeField: (
                                <i
                                    style={{ top: "14px" }}
                                    className="ti ti-search position-absolute start-0 translate-middle-y fs-6 text-dark ms-3 relative"
                                ></i>
                            ),
                            classField: "product-search ps-5",
                            styleField: { marginTop: "-20px" },
                        },
                    ],
                ],
            },
        ],
    }), [statusFilter, statusSelectOptions, t]);

    return (
        <BasicCRUD
            key={`users-${statusFilter}`}
            title={t.title}
            subTitle={t.subTitle}
            homeURL={t.homeURL}
            addTitle={t.addTitle}
            editTitle={t.editTitle}
            disable={userAddDisableConfig || undefined}
            object={object}
            args={listArgs}
            filters={listArgs}
            searchFields={searchFieldsConfig}
            columns={columns}
            fields={fields}
            actions={actions}
            fieldsData={fieldsData}
            setFieldsData={setFieldsData}
            response={response}
            setResponse={setResponse}
            executeBeforeEdit={executeBeforeEdit}
            executeBeforeSave={executeBeforeSave}
            ownHandler={true}
            customAddFunction={openInviteDialog}
            tableAction={handleTableAction}
            viewModes={["table", "blocks"]}
            renderItem={renderUserCard}
            cardLayout={{ columnClass: "col-12 col-lg-6 col-xxl-4" }}
        >
            {response.status === 1 ? (
                <FadeAlert response={response} resetResponse={() => setResponse([])}>
                    {response.created === 1 && (<><strong>{t.toastEntity}</strong> {t.createdOk}</>)}
                    {response.updated === 1 && (<><strong>{t.toastEntity}</strong> {t.updatedOk}</>)}
                    {response.delete === 1 && (<><strong>{t.toastEntity}</strong> {t.deletedOk}</>)}
                    {response.status_changed === 1 && (<>{response.message || statusResponse.message || t.inviteStatusSuccess}</>)}
                </FadeAlert>
            ) : null}
        </BasicCRUD>
    );
};

export default UsersListTab;
